import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertTriangle, Calendar, Download, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useSortableTable, SortableTh } from '../utils/useSortableTable';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_GR = {
  1: 'ΙΑΝΟΥΑΡΙΟΣ', 2: 'ΦΕΒΡΟΥΑΡΙΟΣ', 3: 'ΜΑΡΤΙΟΣ', 4: 'ΑΠΡΙΛΙΟΣ',
  5: 'ΜΑΙΟΣ', 6: 'ΙΟΥΝΙΟΣ', 7: 'ΙΟΥΛΙΟΣ', 8: 'ΑΥΓΟΥΣΤΟΣ',
  9: 'ΣΕΠΤΕΜΒΡΙΟΣ', 10: 'ΟΚΤΩΒΡΙΟΣ', 11: 'ΝΟΕΜΒΡΙΟΣ', 12: 'ΔΕΚΕΜΒΡΙΟΣ'
};

const PendingPage = () => {
  const [pendingMonth, setPendingMonth] = useState(new Date().getMonth() + 1);
  const [pendingYear, setPendingYear] = useState(new Date().getFullYear());
  const [pendingReadings, setPendingReadings] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { sortConfig: sortPending, handleSort: handleSortPending, sortData: sortPendingData } = useSortableTable();
  const { sortConfig: sortExpiring, handleSort: handleSortExpiring, sortData: sortExpiringData } = useSortableTable();
  const { sortConfig: sortInvoices, handleSort: handleSortInvoices, sortData: sortInvoicesData } = useSortableTable();

  useEffect(() => {
    fetchData();
  }, [pendingMonth, pendingYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [pendingRes, expiringRes, invoicesRes] = await Promise.all([
        axios.get(
          `${API_URL}/readings/pending?month=${pendingMonth}&year=${pendingYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${API_URL}/machines/expiring`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${API_URL}/invoices/pending?month=${pendingMonth}&year=${pendingYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);
      
      setPendingReadings(pendingRes.data);
      setExpiringContracts(expiringRes.data);
      setPendingInvoices(invoicesRes.data?.items || []);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης δεδομένων');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPending = () => {
    if (pendingReadings.length === 0) {
      toast.warning('Δεν υπάρχουν δεδομένα για εξαγωγή');
      return;
    }

    // Prepare data for export (remove 'id' column)
    const exportData = pendingReadings.map(({ id, ...rest }) => ({
      'ΠΕΛΑΤΗΣ': rest.company_name,
      'S/N': rest.machine_serial,
      'ΜΑΡΚΑ': rest.brand,
      'ΜΟΝΤΕΛΟ': rest.model,
      'ΜΕΘΟΔΟΣ': rest.reading_method,
      'ΛΗΞΗ ΣΥΜΒΑΣΗΣ': rest.contract_end
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'ΕΚΚΡΕΜΟΤΗΤΕΣ');

    // Generate filename
    const filename = `Pending_${MONTHS_GR[pendingMonth]}_${pendingYear}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
    
    toast.success('Το αρχείο εξήχθη επιτυχώς!');
  };

  if (loading) {
    return <div className="flex justify-center py-12 text-lg">Φόρτωση...</div>;
  }

  return (
    <div className="space-y-6 pb-8">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900">⚠️ ΕΚΚΡΕΜΟΤΗΤΕΣ ΜΗΝΙΑΙΩΝ ΜΕΤΡΗΣΕΩΝ</h1>
        <p className="text-gray-600 mt-1">
          ΜΗΧΑΝΗΜΑΤΑ ΠΟΥ <strong>ΔΕΝ</strong> ΕΧΟΥΝ ΑΚΟΜΑ ΚΑΤΑΧΩΡΗΜΕΝΗ ΜΕΤΡΗΣΗ ΓΙΑ ΤΟΝ ΕΠΙΛΕΓΜΕΝΟ ΜΗΝΑ.
        </p>
      </div>

      {/* Period Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Calendar className="h-5 w-5 text-gray-500" />
            <span className="font-medium text-gray-700">Επιλογή Περιόδου:</span>
            <div className="flex gap-3">
              <select
                value={pendingMonth}
                onChange={(e) => setPendingMonth(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-md"
              >
                {Object.entries(MONTHS_GR).map(([num, name]) => (
                  <option key={num} value={num}>{name}</option>
                ))}
              </select>
              <Input
                type="number"
                value={pendingYear}
                onChange={(e) => setPendingYear(parseInt(e.target.value))}
                className="w-32"
                min={2020}
                max={2030}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Readings Section */}
      <Card className="border-l-4 border-l-orange-500">
        <CardHeader>
          <CardTitle>
            📋 ΕΚΚΡΕΜΕΙΣ ΜΕΤΡΗΣΕΙΣ - {MONTHS_GR[pendingMonth]} {pendingYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingReadings.length > 0 ? (
            <>
              <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-orange-600" />
                  <p className="font-semibold text-orange-800">
                    ΥΠΑΡΧΟΥΝ <span className="text-xl">{pendingReadings.length}</span> ΜΗΧΑΝΗΜΑΤΑ ΧΩΡΙΣ ΜΕΤΡΗΣΗ ΑΥΤΟ ΤΟΝ ΜΗΝΑ
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse border">
                  <thead className="bg-gray-100">
                    <tr>
                      <SortableTh columnKey="company_name" sortConfig={sortPending} onSort={handleSortPending} className="border p-2 text-left">ΠΕΛΑΤΗΣ</SortableTh>
                      <SortableTh columnKey="machine_serial" sortConfig={sortPending} onSort={handleSortPending} className="border p-2 text-left">S/N</SortableTh>
                      <SortableTh columnKey="brand" sortConfig={sortPending} onSort={handleSortPending} className="border p-2 text-left">ΜΑΡΚΑ</SortableTh>
                      <SortableTh columnKey="model" sortConfig={sortPending} onSort={handleSortPending} className="border p-2 text-left">ΜΟΝΤΕΛΟ</SortableTh>
                      <SortableTh columnKey="reading_method" sortConfig={sortPending} onSort={handleSortPending} className="border p-2 text-left">ΜΕΘΟΔΟΣ</SortableTh>
                      <SortableTh columnKey="contract_end" sortConfig={sortPending} onSort={handleSortPending} className="border p-2 text-left">ΛΗΞΗ ΣΥΜΒΑΣΗΣ</SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortPendingData(pendingReadings).map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border p-2">{item.company_name}</td>
                        <td className="border p-2 font-mono text-sm">{item.machine_serial}</td>
                        <td className="border p-2">{item.brand}</td>
                        <td className="border p-2">{item.model}</td>
                        <td className="border p-2">{item.reading_method}</td>
                        <td className="border p-2">{item.contract_end || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button
                onClick={handleExportPending}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
              >
                <Download className="h-4 w-4 mr-2" />
                📥 ΕΞΑΓΩΓΗ ΕΚΚΡΕΜΟΤΗΤΩΝ (Excel)
              </Button>
            </>
          ) : (
            <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
              <div className="flex items-center justify-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <p className="text-lg font-semibold text-green-800">
                  ΟΛΑ ΤΑ ΜΗΧΑΝΗΜΑΤΑ ΕΧΟΥΝ ΚΑΤΑΧΩΡΗΜΕΝΗ ΜΕΤΡΗΣΗ ΓΙΑ ΑΥΤΟ ΤΟΝ ΜΗΝΑ!
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expiring Contracts Section */}
      <Card className="border-l-4 border-l-red-500">
        <CardHeader>
          <CardTitle>📅 ΕΠΕΡΧΟΜΕΝΕΣ ΛΗΞΕΙΣ ΣΥΜΒΑΣΕΩΝ (30 ΗΜΕΡΕΣ)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {expiringContracts.length > 0 ? (
            <>
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                  <p className="font-semibold text-red-800">
                    <span className="text-xl">{expiringContracts.length}</span> ΣΥΜΒΑΣΕΙΣ ΛΗΓΟΥΝ ΕΝΤΟΣ 30 ΗΜΕΡΩΝ!
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse border">
                  <thead className="bg-gray-100">
                    <tr>
                      <SortableTh columnKey="company_name" sortConfig={sortExpiring} onSort={handleSortExpiring} className="border p-2 text-left">ΠΕΛΑΤΗΣ</SortableTh>
                      <SortableTh columnKey="machine_serial" sortConfig={sortExpiring} onSort={handleSortExpiring} className="border p-2 text-left">S/N</SortableTh>
                      <SortableTh columnKey="brand" sortConfig={sortExpiring} onSort={handleSortExpiring} className="border p-2 text-left">ΜΑΡΚΑ</SortableTh>
                      <SortableTh columnKey="model" sortConfig={sortExpiring} onSort={handleSortExpiring} className="border p-2 text-left">ΜΟΝΤΕΛΟ</SortableTh>
                      <SortableTh columnKey="contract_end" sortConfig={sortExpiring} onSort={handleSortExpiring} className="border p-2 text-left">ΛΗΞΗ ΣΥΜΒΑΣΗΣ</SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortExpiringData(expiringContracts).map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border p-2">{item.company_name}</td>
                        <td className="border p-2 font-mono text-sm">{item.machine_serial}</td>
                        <td className="border p-2">{item.brand}</td>
                        <td className="border p-2">{item.model}</td>
                        <td className="border p-2 font-semibold text-red-600">
                          {item.contract_end}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
              <div className="flex items-center justify-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <p className="text-lg font-semibold text-green-800">
                  ΔΕΝ ΥΠΑΡΧΟΥΝ ΣΥΜΒΑΣΕΙΣ ΠΟΥ ΛΗΓΟΥΝ ΣΥΝΤΟΜΑ.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invoices Section */}
      <Card className="border-l-4 border-l-amber-500" data-testid="pending-invoices-section">
        <CardHeader>
          <CardTitle>
            🧾 ΕΚΚΡΕΜΗ ΤΙΜΟΛΟΓΙΑ - {MONTHS_GR[pendingMonth]} {pendingYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingInvoices.length > 0 ? (
            <>
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                  <p className="font-semibold text-amber-800">
                    <span className="text-xl">{pendingInvoices.length}</span> ΠΕΛΑΤΕΣ ΧΩΡΙΣ ΤΙΜΟΛΟΓΙΟ ΑΥΤΟ ΤΟΝ ΜΗΝΑ
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse border" data-testid="pending-invoices-table">
                  <thead className="bg-gray-100">
                    <tr>
                      <SortableTh columnKey="company_name" sortConfig={sortInvoices} onSort={handleSortInvoices} className="border p-2 text-left">ΠΕΛΑΤΗΣ</SortableTh>
                      <SortableTh columnKey="afm" sortConfig={sortInvoices} onSort={handleSortInvoices} className="border p-2 text-left">ΑΦΜ</SortableTh>
                      <SortableTh columnKey="phone" sortConfig={sortInvoices} onSort={handleSortInvoices} className="border p-2 text-left">ΤΗΛΕΦΩΝΟ</SortableTh>
                      <SortableTh columnKey="email" sortConfig={sortInvoices} onSort={handleSortInvoices} className="border p-2 text-left">EMAIL</SortableTh>
                      <th className="border p-2 text-center">ΕΝΕΡΓΕΙΑ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortInvoicesData(pendingInvoices).map((item, idx) => (
                      <tr key={item.customer_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border p-2 font-medium">{item.company_name}</td>
                        <td className="border p-2 font-mono">{item.afm || '-'}</td>
                        <td className="border p-2">{item.phone || '-'}</td>
                        <td className="border p-2">{item.email || '-'}</td>
                        <td className="border p-2 text-center">
                          <Button
                            size="sm"
                            data-testid={`mark-invoice-pending-${item.customer_id}`}
                            className="bg-green-600 hover:bg-green-700"
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              try {
                                await axios.post(`${API_URL}/invoices/mark`,
                                  { customer_id: item.customer_id, year: pendingYear, month: pendingMonth },
                                  { headers: { Authorization: `Bearer ${token}` } });
                                toast.success(`Σημειώθηκε τιμολόγιο: ${item.company_name}`);
                                fetchData();
                              } catch (e) {
                                toast.error('Σφάλμα: ' + (e.response?.data?.detail || ''));
                              }
                            }}
                          >
                            ΣΗΜΕΙΩΣΗ
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
              <div className="flex items-center justify-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <p className="text-lg font-semibold text-green-800">
                  ΟΛΟΙ ΟΙ ΠΕΛΑΤΕΣ ΕΧΟΥΝ ΤΙΜΟΛΟΓΗΘΕΙ ΓΙΑ ΑΥΤΟ ΤΟΝ ΜΗΝΑ!
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingPage;
